import { BookId } from '../../../../funnel/src/app/data/books';

// Every book sample the romance funnel surfaces (book1-3 quiz steps and the
// paywall sample section) comes from the pools in this file, so no
// non-fiction or genre-mismatched titles can leak in from the shared maps
// (motivationBookIdMap, defaultBookIds, nonFictionInterestBookIdsMap). The
// funnel serves romance listeners of any gender; ordering below is by genre
// affinity, not by the gender of who listened.
//
// Percentages are Amplitude 👍-rates (yes/(yes+no), excluding shrugs) on
// kindle-funnel book samples, 30 days to 2026-07-27 (charts n0942qk7 all
// women / y7uippzc women 55+ / hwi1h65y women under 55) - the sample pool
// available at build time skewed ~89% women, so these rates are read as
// genre-affinity data, not as evidence the audience is women. The funnel's
// Meta traffic converts mostly at 45+ (65+ alone is ~46% of purchases), so
// 55+ rates are weighted when two titles are close.

export type RomanceSubgenreId =
  | 'contemporaryRomance'
  | 'romantasy'
  | 'darkRomance'
  | 'historicalRomance'
  | 'smallTownRomance'
  | 'other';

// Spec order for the romanceSubgenres quiz step.
export const romanceSubgenreIds: readonly RomanceSubgenreId[] = [
  'contemporaryRomance',
  'romantasy',
  'darkRomance',
  'historicalRomance',
  'smallTownRomance',
  'other',
];

// Titles served per picked subgenre, best 👍-rate first.
export const romanceSubgenreBookIdsMap: Record<
  Exclude<RomanceSubgenreId, 'other'>,
  readonly BookId[]
> = {
  contemporaryRomance: ['itEndsWithUs', 'meBeforeYou'], // 75.1% / 74.5%
  romantasy: ['onyxStorm', 'aCourtOfThornsAndRoses'], // ad flagship / 72.0%
  darkRomance: ['butcherAndBlackbird'],
  historicalRomance: ['theBookThief', 'theHelp'], // 62.4% / 72.8%
  smallTownRomance: ['whereTheCrawdadsSing'], // 76.5%
};

// The always-appended tail of the sample pool (and the whole pool when the
// listener picked "Something else / not sure"), ordered by 👍-rate.
export const romanceFeaturedBookIds: readonly BookId[] = [
  'whereTheCrawdadsSing', // 76.5%
  'itEndsWithUs', // 75.1% overall, 78.7% with 55+
  'goneGirl', // 74.5%
  'meBeforeYou', // 74.5%
  'theHelp', // 72.8%
  'aCourtOfThornsAndRoses', // 72.0%, 74.5% with under-55
  'onyxStorm', // flagship romantasy ad creative, no sample data yet
  'theSevenHusbandsOfEvelynHugo', // 68.7%
];

// Interleaves one title per picked subgenre per round (same diversity
// pattern as interleaveBookIds in funnel/src/app/data/books.ts) so a
// listener who picked several subgenres hears a spread instead of two books
// from one shelf. 'other' has no book pool and is ignored.
export function getRomanceBookIdsForSubgenres(subgenreIds: readonly RomanceSubgenreId[]): BookId[] {
  const result: BookId[] = [];
  const groups = subgenreIds
    .filter((id): id is Exclude<RomanceSubgenreId, 'other'> => id !== 'other')
    .map(id => romanceSubgenreBookIdsMap[id] || []);
  const maxGroupLength = Math.max(0, ...groups.map(group => group.length));

  for (let index = 0; index < maxGroupLength; index++) {
    groups.forEach(group => {
      const bookId = group[index];

      if (bookId && !result.includes(bookId)) {
        result.push(bookId);
      }
    });
  }

  return result;
}
